import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, FormControl, InputLabel, Select, MenuItem, Button, Table, TableBody, TableCell, TableHead, TableRow, Paper } from '@mui/material';
import { apiFetchJson } from '../fetchWrapper';

export default function RegisterOfficeAttendance() {
  const [offices, setOffices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [attendanceType, setAttendanceType] = useState('OFFICE_STAFF');
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    apiFetchJson('/api/offices').then(setOffices).catch(() => setOffices([]));
    apiFetchJson('/api/employees').then(setEmployees).catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    if (!selectedOffice) {
      setRecent([]);
      return;
    }
    fetchRecent();
  }, [selectedOffice, attendanceType]);

  const fetchRecent = async () => {
    const type = attendanceType;
    const res = await apiFetchJson(`/api/attendances?officeId=${selectedOffice}&attendanceType=${type}`);
    setRecent(res || []);
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    if (!emp) return id;
    const p = emp.user && emp.user.person ? emp.user.person : null;
    if (!p) return emp.id;
    return `${p.firstname} ${p.lastnameFirst || ''}`.trim();
  };

  const getOfficeName = (id) => {
    const o = offices.find(x => x.id === id);
    return o ? o.name : id;
  };

  const typeLabel = (t) => {
    if (!t) return '';
    if (t === 'OFFICE_STAFF') return 'Office Staff';
    if (t === 'SUPERVISOR') return 'Supervisor';
    return t;
  };

  const doCheck = (mode) => {
    if (!selectedOffice || !selectedEmployee) {
      alert('Selecciona oficina y empleado');
      return;
    }

    if (!navigator.geolocation) {
      alert('Geolocalización no soportada');
      return;
    }

    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      const payload = {
        employeeId: Number(selectedEmployee),
        officeId: Number(selectedOffice),
        attendanceType,
        attendanceStatus: 'ON_TIME',
        latitude,
        longitude,
        checkIn: mode === 'in' ? new Date().toISOString() : undefined,
        checkOut: mode === 'out' ? new Date().toISOString() : undefined,
      };

      if (mode === 'out') {
        const entries = await apiFetchJson(`/api/attendances?employeeId=${selectedEmployee}`) || [];
        const open = entries.find(e => e.checkIn && !e.checkOut && e.officeId === Number(selectedOffice));
        if (open) {
          const updated = { ...open, checkOut: new Date().toISOString() };
          await apiFetchJson(`/api/attendances/${open.id}`, { method: 'PUT', body: JSON.stringify(updated) });
          fetchRecent();
          return;
        }
        alert('No se encontró un registro abierto para este empleado en la oficina seleccionada.');
        return;
      }

      const created = await apiFetchJson('/api/attendances', { method: 'POST', body: JSON.stringify(payload) });
      setRecent(prev => [created, ...prev.filter(r => r.id !== created.id)]);
    }, (err) => {
      alert('Error geolocalización: ' + err.message);
    });
  };

  const buttonsDisabled = !selectedOffice || !selectedEmployee || !navigator.geolocation;

  return (
    <Box sx={{ mt: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h6">Registro Oficina / Supervisión</Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Oficina</InputLabel>
              <Select value={selectedOffice} label="Oficina" onChange={e => setSelectedOffice(e.target.value)}>
                <MenuItem value="">Ninguna</MenuItem>
                {offices.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Empleado</InputLabel>
              <Select value={selectedEmployee} label="Empleado" onChange={e => setSelectedEmployee(e.target.value)}>
                <MenuItem value="">Ninguno</MenuItem>
                {employees.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.user && emp.user.person ? `${emp.user.person.firstname} ${emp.user.person.lastnameFirst || ''}` : emp.id}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Tipo</InputLabel>
              <Select value={attendanceType} label="Tipo" onChange={e => setAttendanceType(e.target.value)}>
                <MenuItem value={'OFFICE_STAFF'}>Office Staff</MenuItem>
                <MenuItem value={'SUPERVISOR'}>Supervisor</MenuItem>
              </Select>
            </FormControl>

            <Button variant="contained" color="success" onClick={() => doCheck('in')} disabled={buttonsDisabled}>Entrada</Button>
            <Button variant="contained" color="error" onClick={() => doCheck('out')} disabled={buttonsDisabled}>Salida</Button>

          </Box>

          <Paper sx={{ mt: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Empleado</TableCell>
                  <TableCell>Oficina</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>CheckIn</TableCell>
                  <TableCell>CheckOut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recent.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 2 }}>No hay registros recientes</TableCell>
                  </TableRow>
                )}
                {recent.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{getEmployeeName(r.employeeId)}</TableCell>
                    <TableCell>{getOfficeName(r.officeId)}</TableCell>
                    <TableCell>{typeLabel(r.attendanceType)}</TableCell>
                    <TableCell>{r.attendanceStatus || '—'}</TableCell>
                    <TableCell>{r.checkIn ? new Date(r.checkIn).toLocaleString() : '—'}</TableCell>
                    <TableCell>{r.checkOut ? new Date(r.checkOut).toLocaleString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>
    </Box>
  );
}
